import React from 'react';
import { Box } from '@mui/material';

/**
 * ModalGrid - A professional grid system specifically designed for Dialog/Modal components
 *
 * Solves Material-UI Grid issues inside dialogs by using CSS Flexbox with explicit calculations
 * Provides reliable responsive behavior with proper field widths
 */

const ModalGrid = ({
  container = false,
  item = false,
  spacing = 2,
  mobile = 12,
  tablet = null,
  desktop = null,
  children,
  sx = {},
  ...props
}) => {
  // Container styles for grid wrapper
  const containerStyles = container ? {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    margin: `-${spacing * 4}px`,
    '& > *': {
      padding: `${spacing * 4}px`,
      boxSizing: 'border-box',
    }
  } : {};

  // Item styles for grid children with responsive breakpoints
  const itemStyles = item ? {
    // Mobile first (default)
    width: `${(mobile / 12) * 100}%`,
    flexBasis: `${(mobile / 12) * 100}%`,
    maxWidth: `${(mobile / 12) * 100}%`,

    // Tablet breakpoint (768px and up)
    '@media (min-width: 768px)': tablet ? {
      width: `${(tablet / 12) * 100}%`,
      flexBasis: `${(tablet / 12) * 100}%`,
      maxWidth: `${(tablet / 12) * 100}%`,
    } : {},

    // Desktop breakpoint (1024px and up)
    '@media (min-width: 1024px)': desktop ? {
      width: `${(desktop / 12) * 100}%`,
      flexBasis: `${(desktop / 12) * 100}%`,
      maxWidth: `${(desktop / 12) * 100}%`,
    } : {},
  } : {};

  const combinedStyles = {
    ...containerStyles,
    ...itemStyles,
    ...sx
  };

  return (
    <Box sx={combinedStyles} {...props}>
      {children}
    </Box>
  );
};

// Convenience components for common layouts
export const ModalGridContainer = ({ spacing = 2, children, sx = {}, ...props }) => (
  <ModalGrid container spacing={spacing} sx={sx} {...props}>
    {children}
  </ModalGrid>
);

export const ModalGridItem = ({
  mobile = 12,
  tablet = null,
  desktop = 6,
  children,
  sx = {},
  ...props
}) => (
  <ModalGrid
    item
    mobile={mobile}
    tablet={tablet}
    desktop={desktop}
    sx={sx}
    {...props}
  >
    {children}
  </ModalGrid>
);

// Pre-configured layouts for common form patterns
export const ModalFormRow = ({ children, spacing = 2 }) => (
  <ModalGridContainer spacing={spacing}>
    {children}
  </ModalGridContainer>
);

export const ModalFormField = ({ fullWidth = false, children, ...props }) => (
  <ModalGridItem
    mobile={12}
    desktop={fullWidth ? 12 : 6}
    {...props}
  >
    {children}
  </ModalGridItem>
);

export default ModalGrid;